import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import { debounce } from 'lodash';

import Search from '../../../assets/images/icons/search.svg';
import Loading from '../../ui/loading';

import $ from 'jquery';

function SearchIcon() {
  return (
    <Search
      className='absolute right-3 top-1/2 transform -translate-y-1/2 fill-grey-700'
      width="13"
      height="13"
    />
  );
}

class AutosuggestWrapper extends Component {
  constructor() {
    super();

    this.state = {
      suggestions: [],
      showStatusText: false,
      value: '',
      isLoading: false
    };

    this.onSuggestionsFetchRequested = debounce(this.onSuggestionsFetchRequested, 300);
  }

  // Prevent form from being submitted when enter is pressed.
  onKeyPress(e) {
    if (e.charCode === 13) {
      e.preventDefault();
    }
  }

  onChange(_event, { newValue }) {
    this.setState({
      value: newValue
    });
  }

  filterSuggestions(suggestions) {
    const shouldRenderSuggestion = this.props.shouldRenderSuggestion;
    return suggestions.filter(sug => shouldRenderSuggestion(sug));
  }

  onSuggestionsFetchRequested({ value, reason }) {
    if (this.request) {
      this.request.abort();
      this.request = null;
    }

    // Do nothing if the provided input is too small, otherwise we return
    // a huge amount of data and it isn't very performant.
    //
    // For now, this makes the CPV autosuggest filter less useful, but we'll
    // worry about that later.
    if (value.length < this.props.minimumInputLength) {
      return this.setState({ suggestions: [], isLoading: false });
    }

    // If a fetch request has been triggered because the user has clicked on a result
    // we don't need to make another request to
    if (reason === 'suggestion-selected') {
      const filteredData = this.filterSuggestions(this.state.suggestions);
      return this.setState({ suggestions: filteredData, isLoading: false });
    }

    this.setState({ isLoading: true });

    this.request = $.ajax({
      type: 'GET',
      url: this.props.url,
      data: {
        q: value
      },
      success: function(data) {
        if (this.props.includeInputAsSuggestion) {
          data.unshift(value);
        }
        // Remove any matches that need to be ignored
        const filteredData = this.filterSuggestions(data);
        this.setState({ suggestions: filteredData, isLoading: false });
      }.bind(this)
    });
  }

  onSuggestionsClearRequested() {
    this.setState({
      suggestions: []
    });
  }

  onSuggestionSelected(event, { method, suggestion }) {
    if (method === 'enter') {
      event.preventDefault();
    }
    this.props.onChange(suggestion);
  }

  getSuggestionValue(_option) {
    return this.state.value;
  }

  renderInputComponent(inputProps) {
    return (
      <div className='relative'>
        <input {...inputProps} />
        <SearchIcon />
      </div>
    );
  }

  renderSuggestionsContainer({ containerProps, children }) {
    const { showStatusText } = this.state;

    return (
      <div {... containerProps}
        className={
          `mt-1 max-h-60 rounded border-grey-300 bg-text-grey-700
          shadow-md overscroll-y-contain overflow-y-scroll
          hover:overscroll-y-contain hover:overflow-y-scroll`
        }>
        {showStatusText &&
          <div className='w-full px-2 py-1.5 text-green-500 font-medium empty:hidden'>
            {this.statusText()}
          </div>
        }
        {children}
      </div>
    );
  }

  statusText() {
    const { isLoading } = this.state;

    if (isLoading) { return <Loading />; }

    if (!this.state.value) { return 'Type to search...'; }

    // If you have any, just display suggestions and no status.
    if (this.state.suggestions.length > 0) { return; }

    if (this.state.value.length < this.props.minimumInputLength) {
      return 'Enter more characters...';
    }
    if (this.state.value.length > this.props.minimumInputLength) {
      return 'No results';
    }
  }

  render() {
    const { value, suggestions } = this.state;

    // Autosuggest will pass through all these props to the input element.
    const inputProps = {
      placeholder: this.props.placeholder,
      onChange: this.onChange.bind(this),
      onKeyPress: this.onKeyPress.bind(this),
      // We only want to show the status text when the user has clicked on the input
      onFocus: () => this.setState({ showStatusText: true }),
      onBlur: () => this.setState({ showStatusText: false }),
      name: this.props.name,
      value,
      className: 'rounded border-none w-full p-2 pr-8 bg-grey-100 text-base text-grey-700 focus:ring-0 space-y-2'
    };

    // Finally, render it!
    return (
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested.bind(this)}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested.bind(this)}
          alwaysRenderSuggestions={true}
          onSuggestionSelected={this.onSuggestionSelected.bind(this)}
          getSuggestionValue={this.getSuggestionValue.bind(this)}
          renderSuggestion={this.props.renderSuggestion.bind(this)}
          inputProps={inputProps}
          renderInputComponent={this.renderInputComponent.bind(this)}
          renderSuggestionsContainer={this.renderSuggestionsContainer.bind(this)}
        />
    );
  }
}

export default AutosuggestWrapper;

AutosuggestWrapper.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
  suggestionValue: PropTypes.func.isRequired,
  renderSuggestion: PropTypes.func.isRequired,
  shouldRenderSuggestion: PropTypes.func.isRequired,
  // Set this to true to include the input as a suggestion at the top of the
  // results. Defaults to true
  includeInputAsSuggestion: PropTypes.bool,
  minimumInputLength: PropTypes.number
};

AutosuggestWrapper.defaultProps = {
  includeInputAsSuggestion: true,
  placeholder: 'Type to search...',
  minimumInputLength: 3
};
